/**
 * ============================================================
 * PGLP | MONTE HERMOM
 * COMPONENTE: TarefasParadigma_inicial (v2.0.0)
 * ------------------------------------------------------------
 * REESCRITO: agora usa o backend real (PainelTarefas).
 * O nome foi mantido para não quebrar imports existentes.
 *
 * PROPS:
 *   codigo      — ex: 'MH-2026-0006'  (auto-detecta se omitido)
 *   laudoUuid   — opcional
 *   imovelId    — opcional (filtra por imóvel)
 *   imovelUuid  — opcional
 *   endereco    — opcional (pré-preenche modal de agendamento)
 *   processo    — opcional
 * ============================================================
 */

import React from 'react';
import PainelTarefas from './PainelTarefas';

/* ============================================================
   AUTO-DETECÇÃO DE CONTEXTO
   ============================================================ */

function detectarCodigo() {
  if (typeof window === 'undefined') return null;

  // 1. Query string: ?codigo=MH-2026-0006
  try {
    const params = new URLSearchParams(window.location.search);
    const q = params.get('codigo') || params.get('reportNumber') || params.get('laudo');
    if (q) return q;
  } catch { /* ignora */ }

  // 2. localStorage (chaves comuns do sistema)
  const chaves = [
    'mh_laudo_ativo',
    'laudoAtivo',
    'codigo_mh',
    'reportNumber',
    'laudo.codigo_mh',
  ];
  for (const k of chaves) {
    const v = localStorage.getItem(k);
    if (v && typeof v === 'string' && v.trim() !== '') {
      return v.trim();
    }
  }

  // 3. SessionStorage (fallback)
  try {
    const s = sessionStorage.getItem('laudoAtivo');
    if (s) return s;
  } catch { /* ignora */ }

  return null;
}

function detectarImovelId() {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const q = params.get('imovelId') || params.get('imovel_id');
    if (q) return Number(q);
  } catch { /* ignora */ }
  const k = localStorage.getItem('mh_imovel_ativo_id');
  return k ? Number(k) : null;
}

function detectarImovelUuid() {
  if (typeof window === 'undefined') return null;
  const k = localStorage.getItem('mh_imovel_ativo_uuid');
  return k || null;
}

/* ============================================================
   COMPONENTE
   ============================================================ */

export default function TarefasParadigma_inicial({
  codigo,
  laudoUuid = null,
  imovelId,
  imovelUuid,
  endereco = '',
  processo = '',
}) {
  const codigoFinal = codigo || detectarCodigo();
  const imovelIdFinal = (imovelId !== undefined && imovelId !== null)
    ? imovelId
    : detectarImovelId();
  const imovelUuidFinal = imovelUuid || detectarImovelUuid();

  /* ---------- Estado sem contexto ---------- */
  if (!codigoFinal) {
    return (
      <div className="w-full h-full flex flex-col fade-in-soberano">
        <HeaderFluxo />
        <div className="flex-1 flex flex-col items-center justify-center bg-slate-50 rounded-[3rem] p-12">
          <span className="text-5xl mb-6">📋</span>
          <p className="text-slate-700 font-black uppercase text-sm tracking-wider mb-2">
            Nenhum laudo ativo
          </p>
          <p className="text-slate-400 font-medium text-[11px] text-center max-w-md leading-relaxed">
            Abra um laudo para que as tarefas sejam carregadas automaticamente.
            Ou passe o código via prop:
            <code className="block mt-3 bg-slate-900 text-emerald-400 px-4 py-2 rounded-lg font-mono text-[10px]">
              {'<TarefasParadigma_inicial codigo="MH-2026-0006" />'}
            </code>
          </p>
        </div>
      </div>
    );
  }

  /* ---------- Render principal ---------- */
  return (
    <div className="w-full h-full flex flex-col fade-in-soberano">
      <HeaderFluxo codigo={codigoFinal} />

      <div className="flex-1 min-h-0">
        <PainelTarefas
          codigo={codigoFinal}
          laudoUuid={laudoUuid}
          imovelId={imovelIdFinal}
          imovelUuid={imovelUuidFinal}
          endereco={endereco}
          processo={processo}
        />
      </div>
    </div>
  );
}

/* ============================================================
   HEADER (visual idêntico ao componente antigo)
   ============================================================ */

function HeaderFluxo({ codigo }) {
  return (
    <div className="flex items-center justify-between mb-8 pb-6 border-b-2 border-slate-100">
      <div className="flex items-center gap-6">
        <div className="p-4 bg-rose-900 rounded-3xl shadow-lg text-white border border-rose-700">
          <i data-lucide="list-checks" className="w-8 h-8"></i>
        </div>
        <div>
          <h3 className="font-black uppercase text-slate-900 text-3xl tracking-tighter leading-none">
            Fluxo de Tarefas
          </h3>
          <p className="text-rose-900 font-black text-[10px] uppercase tracking-[0.4em] mt-1">
            Protocolo de Integridade M. Hermom
          </p>
        </div>
      </div>

      {codigo && (
        <div className="text-right">
          <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
            Laudo
          </p>
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">
            {codigo}
          </p>
        </div>
      )}
    </div>
  );
}